// v10/src/components/admin/lifespan/CreateComponentModal.js
//
// Modal "Agregar componente" del módulo Vida límite (A6.1) — Tarea 2.
// Toggle entre dos modos:
//   • catalog → seleccionar de los 21 items del catálogo, prellenar TBOs
//   • custom  → ingresar manualmente nombre + categoría + TBOs (Formulario 337)
//
// Endpoints consumidos:
//   GET  /api/component-catalog              (carga inicial — universales + específicos)
//   POST /api/aircraft-components            (creación con source=catalog|custom)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Plus, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import componentLifespanService from '../../../services/componentLifespanService';

const CATEGORY_KEYS = [
  'engine', 'propeller', 'magneto', 'elt_battery', 'fire_extinguisher',
  'seatbelt', 'pitot_tube', 'spark_plug', 'oil_filter', 'other',
];

const todayIso = () => new Date().toISOString().split('T')[0];

const matchesAircraft = (catalogItem, aircraft) => {
  if (!aircraft) return false;
  const itemMfg = (catalogItem.aircraft_manufacturer || '').trim().toLowerCase();
  const itemPat = (catalogItem.aircraft_model_pattern || '').trim().toLowerCase();
  if (!itemMfg && !itemPat) return false; // universal — manejado aparte
  const acMfg = (aircraft.manufacturer || '').trim().toLowerCase();
  const acModel = (aircraft.model_name || '').trim().toLowerCase();
  if (itemMfg && itemMfg !== acMfg) return false;
  if (itemPat && !acModel.includes(itemPat)) return false;
  return true;
};

const isUniversal = (catalogItem) =>
  !catalogItem.aircraft_manufacturer && !catalogItem.aircraft_model_pattern;

const CreateComponentModal = ({
  aircraft,
  onSuccess,
  onClose,
  darkMode,
}) => {
  const { t } = useTranslation('admin');

  const [mode, setMode] = useState('catalog');
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [form, setForm] = useState({
    catalog_item_id: '',
    name: '',
    category: '',
    manufacturer: '',
    serial_number: '',
    tbo_hours: '',
    tbo_calendar_months: '',
    installed_at_date: todayIso(),
    installed_at_horometer: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // ── Carga del catálogo al montar ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const res = await componentLifespanService.getCatalog({ is_active: 'true' });
        if (cancelled) return;
        const items = res?.data?.data || [];
        setCatalog(items);
      } catch (_) {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── ESC para cerrar ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  // ── Items relevantes para esta aeronave ──────────────────────────
  const { specificItems, universalItems } = useMemo(() => {
    const universals = catalog.filter(isUniversal);
    const specifics = catalog.filter((item) => matchesAircraft(item, aircraft));
    return { specificItems: specifics, universalItems: universals };
  }, [catalog, aircraft]);

  const selectedCatalogItem = useMemo(
    () => catalog.find((c) => c.id === form.catalog_item_id) || null,
    [catalog, form.catalog_item_id]
  );

  // Cuando cambia la selección del catálogo, prellenar TBOs.
  useEffect(() => {
    if (mode !== 'catalog' || !selectedCatalogItem) return;
    setForm((f) => ({
      ...f,
      tbo_hours: f.tbo_hours === '' && selectedCatalogItem.default_tbo_hours != null
        ? String(selectedCatalogItem.default_tbo_hours)
        : f.tbo_hours,
      tbo_calendar_months: f.tbo_calendar_months === '' && selectedCatalogItem.default_tbo_calendar_months != null
        ? String(selectedCatalogItem.default_tbo_calendar_months)
        : f.tbo_calendar_months,
    }));
  }, [mode, selectedCatalogItem]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const switchMode = (next) => {
    if (loading || next === mode) return;
    setMode(next);
    setForm((f) => ({
      ...f,
      catalog_item_id: '',
      name: '',
      category: '',
      manufacturer: '',
      tbo_hours: '',
      tbo_calendar_months: '',
    }));
  };

  // ── Validación ───────────────────────────────────────────────────
  const validation = useMemo(() => {
    const errors = [];

    if (mode === 'catalog' && !form.catalog_item_id) {
      errors.push(t('lifespan_validation_catalog_item_required'));
    }
    if (mode === 'custom') {
      if (!form.name.trim()) errors.push(t('lifespan_validation_name_required'));
      if (!form.category) errors.push(t('lifespan_validation_category_required'));
    }

    const tboH = form.tbo_hours === '' ? null : Number(form.tbo_hours);
    const tboM = form.tbo_calendar_months === '' ? null : Number(form.tbo_calendar_months);
    const hasH = tboH !== null && Number.isFinite(tboH) && tboH > 0;
    const hasM = tboM !== null && Number.isFinite(tboM) && tboM > 0;

    if (tboH !== null && Number.isFinite(tboH) && tboH <= 0) {
      errors.push(t('lifespan_validation_tbo_hours_positive'));
    }
    if (tboM !== null && Number.isFinite(tboM) && tboM <= 0) {
      errors.push(t('lifespan_validation_tbo_months_positive'));
    }
    if (!hasH && !hasM) {
      errors.push(t('lifespan_validation_tbo_required'));
    }

    if (!form.installed_at_date) {
      errors.push(t('lifespan_validation_installed_date_required'));
    } else if (form.installed_at_date > todayIso()) {
      errors.push(t('lifespan_validation_installed_date_future'));
    }

    if (form.installed_at_horometer === '') {
      errors.push(t('lifespan_validation_installed_horometer_required'));
    } else {
      const h = Number(form.installed_at_horometer);
      if (!Number.isFinite(h) || h < 0) {
        errors.push(t('lifespan_validation_installed_horometer_negative'));
      }
    }

    return { errors, ok: errors.length === 0 };
  }, [form, mode, t]);

  // ── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || loading || !aircraft?.id) return;

    const payload = {
      aircraft_id: aircraft.id,
      source: mode,
      installed_at_date: form.installed_at_date,
      installed_at_horometer: Number(form.installed_at_horometer),
      serial_number: form.serial_number.trim() || null,
      notes: form.notes.trim() || null,
    };

    if (form.tbo_hours !== '') payload.tbo_hours = Number(form.tbo_hours);
    if (form.tbo_calendar_months !== '') payload.tbo_calendar_months = Number(form.tbo_calendar_months);

    if (mode === 'catalog') {
      payload.catalog_item_id = form.catalog_item_id;
    } else {
      payload.catalog_item_id = null;
      payload.name = form.name.trim();
      payload.category = form.category;
      payload.manufacturer = form.manufacturer.trim() || null;
    }

    setLoading(true); setError('');
    try {
      await componentLifespanService.createComponent(payload);
      if (!isMounted.current) return;
      onSuccess?.();
      onClose();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.response?.data?.message || t('lifespan_validation_network_error'));
      setLoading(false);
    }
  };

  // ── Estilos compartidos ──────────────────────────────────────────
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const showCatalogFields = mode === 'catalog';
  const showCustomFields = mode === 'custom';
  const tboHoursDefault = selectedCatalogItem?.default_tbo_hours;
  const tboMonthsDefault = selectedCatalogItem?.default_tbo_calendar_months;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(79,70,229,0.3)',
              }}
            >
              <Wrench style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('component_create_modal_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {aircraft?.registration ? `${aircraft.registration} · ${aircraft.manufacturer || ''} ${aircraft.model_name || ''}`.trim() : t('component_create_modal_subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Toggle catalog/custom */}
        <div
          role="tablist"
          style={{
            display: 'inline-flex', padding: 4, borderRadius: 12, gap: 4,
            background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
            marginBottom: 18,
          }}
        >
          {[
            { id: 'catalog', label: t('component_create_modal_mode_catalog') },
            { id: 'custom',  label: t('component_create_modal_mode_custom') },
          ].map((opt) => {
            const isSel = opt.id === mode;
            return (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={isSel}
                onClick={() => switchMode(opt.id)}
                disabled={loading}
                style={{
                  padding: '8px 14px', borderRadius: 9, border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  background: isSel ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white') : 'transparent',
                  color: isSel ? (darkMode ? 'white' : '#0f172a') : fgMuted,
                  boxShadow: isSel ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                  transition: 'all 0.18s',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Modo catálogo: selector */}
          {showCatalogFields && (
            <div style={{ marginBottom: 14 }}>
              <p style={labelStyle}>{t('component_create_modal_label_catalog_item')}</p>
              <select
                value={form.catalog_item_id}
                onChange={(e) => setField('catalog_item_id', e.target.value)}
                disabled={catalogLoading}
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
              >
                <option value="">
                  {catalogLoading
                    ? t('component_create_modal_catalog_loading')
                    : t('component_create_modal_placeholder_catalog_item')}
                </option>
                {specificItems.length > 0 && (
                  <optgroup label={t('component_create_modal_catalog_specific_group')}>
                    {specificItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}{item.manufacturer ? ` (${item.manufacturer})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {universalItems.length > 0 && (
                  <optgroup label={t('component_create_modal_catalog_universal_group')}>
                    {universalItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}{item.manufacturer ? ` (${item.manufacturer})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {!catalogLoading && specificItems.length === 0 && universalItems.length === 0 && (
                <p style={{ margin: '6px 0 0', fontSize: 12, color: fgFaint }}>
                  {t('component_create_modal_catalog_empty')}
                </p>
              )}
            </div>
          )}

          {/* Modo custom: nombre + categoría + manufacturer */}
          {showCustomFields && (
            <>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('component_create_modal_label_name')}</p>
                <input
                  type="text" maxLength={150} style={inputStyle}
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder={t('component_create_modal_placeholder_name')}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('component_create_modal_label_category')}</p>
                <select
                  value={form.category}
                  onChange={(e) => setField('category', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('component_create_modal_placeholder_category')}</option>
                  {CATEGORY_KEYS.map((k) => (
                    <option key={k} value={k}>{t(`component_category_${k}`)}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('component_create_modal_label_manufacturer')}</p>
                <input
                  type="text" maxLength={100} style={inputStyle}
                  value={form.manufacturer}
                  onChange={(e) => setField('manufacturer', e.target.value)}
                  placeholder={t('component_create_modal_placeholder_manufacturer')}
                />
              </div>
            </>
          )}

          {/* Serial number — ambos modos */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('component_create_modal_label_serial_number')}</p>
            <input
              type="text" maxLength={100} style={inputStyle}
              value={form.serial_number}
              onChange={(e) => setField('serial_number', e.target.value)}
              placeholder={t('component_create_modal_placeholder_serial_number')}
            />
          </div>

          {/* TBOs en grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_tbo_hours')}</p>
              <input
                type="number" min="0" step="0.1" style={inputStyle}
                value={form.tbo_hours}
                onChange={(e) => setField('tbo_hours', e.target.value)}
                placeholder={t('component_create_modal_placeholder_tbo_hours')}
              />
              {showCatalogFields && tboHoursDefault != null && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                  {t('component_create_modal_hint_tbo_hours_default', { value: tboHoursDefault })}
                </p>
              )}
            </div>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_tbo_months')}</p>
              <input
                type="number" min="0" step="1" style={inputStyle}
                value={form.tbo_calendar_months}
                onChange={(e) => setField('tbo_calendar_months', e.target.value)}
                placeholder={t('component_create_modal_placeholder_tbo_months')}
              />
              {showCatalogFields && tboMonthsDefault != null && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                  {t('component_create_modal_hint_tbo_months_default', { value: tboMonthsDefault })}
                </p>
              )}
            </div>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 11, color: fgFaint }}>
            {t('component_create_modal_hint_tbo_at_least_one')}
          </p>

          {/* Instalación */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_installed_date')}</p>
              <input
                type="date"
                max={todayIso()}
                style={{ ...inputStyle, cursor: 'text' }}
                value={form.installed_at_date}
                onChange={(e) => setField('installed_at_date', e.target.value)}
              />
            </div>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_installed_horometer')}</p>
              <input
                type="number" min="0" step="0.1" style={inputStyle}
                value={form.installed_at_horometer}
                onChange={(e) => setField('installed_at_horometer', e.target.value)}
                placeholder={t('component_create_modal_placeholder_installed_horometer')}
              />
            </div>
          </div>

          {/* Notas */}
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('component_create_modal_label_notes')}</p>
            <textarea
              rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60, cursor: 'text' }}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder={t('component_create_modal_placeholder_notes')}
            />
          </div>

          {/* Errores cliente-side */}
          {validation.errors.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {/* Error backend */}
          {error && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={loading || !validation.ok}
              whileHover={!loading && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!loading && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: loading || !validation.ok ? 'not-allowed' : 'pointer',
                background: loading || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {loading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Plus style={{ width: 16, height: 16 }} />
              }
              {loading ? t('component_create_modal_btn_saving') : t('component_create_modal_btn_submit')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('lifespan_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CreateComponentModal;
